import React, { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import Table from "react-bootstrap/Table";
import HigherOrderComponent from "./HigherOrderComponent";
import DOMPurify from "dompurify";
import "../../index.css";
import { getOptionEntries, getProductSku, getProductDisplayName } from "../Templates/quoteCalculator";
import { VERSION } from "../../config/Version";

const PACKET_MATERIALS = {
  business: {
    title: "Business Security Camera System Packet",
    coverQuoteTitle: "Business Camera System Quote",
    coverFile: "business-cover-page.html",
    contentFile: "business-packet-contents.html",
    subtitle: "Custom camera solutions for your business",
    brochureUrl: "https://platinumcctv.com/assets/PDFs/AVM-Business-Security-Camera-Systems-sml.pdf",
    bullets: ["AI camera alerts for people and vehicles", "Fast remote access and video searching", "Custom system design, installation, and local support"],
  },
  home: {
    title: "Home Security Camera System Packet",
    coverQuoteTitle: "Home Camera System Quote",
    coverFile: "home-cover-page.html",
    contentFile: "home-packet-contents.html",
    subtitle: "Custom designed camera solutions for your home",
    brochureUrl: "https://platinumcctv.com/assets/PDFs/Home AVM NVR System Brochure - 2025.pdf",
    bullets: ["Proactive alerts for people, vehicles, packages, and property activity", "Remote access from phone, tablet, PC, Mac, TVs, and LCDs", "No monthly fees for viewing and recording"],
  },
  warehouse: {
    title: "Warehouse Security Camera System Packet",
    coverQuoteTitle: "Warehouse Camera System Quote",
    coverFile: "warehouse-cover-page.html",
    contentFile: "warehouse-packet-contents.html",
    subtitle: "Enterprise-grade video security for warehouses",
    brochureUrl: "https://platinumcctv.com/assets/PDFs/AVM-Warehouse-Security-Camera-Systems-sml.pdf",
    bullets: ["Scan through days or weeks of video in seconds", "AI event search for people and vehicles", "Designed for large facilities and operational visibility"],
  },
  property: {
    title: "Property Management Security Camera System Packet",
    coverQuoteTitle: "Property Management Camera System Quote",
    coverFile: "property-cover-page.html",
    contentFile: "property-packet-contents.html",
    subtitle: "Designed for property managers and multi-site operations",
    brochureUrl: "https://platinumcctv.com/assets/PDFs/AVM-Property-Management-Camera-Systems.pdf",
    bullets: ["Find slip-and-fall, dumping, vandalism, and tenant incidents faster", "License plate and AI camera options", "Remote access and technical support for multiple properties"],
  },
  manufacturing: {
    title: "Manufacturing Camera System Packet",
    coverQuoteTitle: "Manufacturing Camera System Quote",
    coverFile: "manufacturing-cover-page.html",
    contentFile: "manufacturing-packet-contents.html",
    subtitle: "Video security and operational visibility for manufacturing",
    brochureUrl: "https://platinumcctv.com/assets/PDFs/AVM-Business-Security-Camera-Systems-sml.pdf",
    bullets: ["AI alerts for restricted areas, bay doors, and after-hours activity", "High-quality recording with fast incident search", "Professional installation and integration support"],
  },
};
const Print = ({ combinedData, whichOption }) => {
  const customerUserData = useSelector((state) => state.counter1.customerData);
  const optionHeading = useSelector((state) => state.counter1.optionHeading);
  const optionDisplayText = useSelector((state) => state.counter1.optionDisplayText || {});
  const optionDiscounts = useSelector((state) => state.counter1.optionDiscounts || {});
  const optionDescription = useSelector(
    (state) => state.counter1.optionDescription
  );
  const totalNoOptions = useSelector((state) => state.counter1.totalNoOptions);
  const quoteVersion = useSelector((state) => state.counter1.quoteVersion || VERSION);
  const [showTableHeader, setShowTableHeader] = useState(true); // Initially show the header
  const headerRenderedRef = useRef({});
  const getPacketTypeFromUrl = () => {
    const normalSearch = new URLSearchParams(window.location.search || "").get("packet");
    if (normalSearch) return normalSearch;

    // HashRouter keeps route query params inside window.location.hash, e.g. #/print?packet=business.
    // window.location.search is empty in that case, so parse the hash directly.
    const hash = window.location.hash || "";
    const queryStart = hash.indexOf("?");
    if (queryStart === -1) return "";
    return new URLSearchParams(hash.slice(queryStart + 1)).get("packet") || "";
  };

  const packetType = (getPacketTypeFromUrl() || "").toLowerCase();
  const selectedPacket = packetType ? PACKET_MATERIALS[packetType] || PACKET_MATERIALS.business : null;
  const shouldAppendPacket = Boolean(selectedPacket?.contentFile);
  const shouldPrependPacketCover = Boolean(selectedPacket?.coverFile);
  const [packetContentHtml, setPacketContentHtml] = useState("");
  const [packetContentLoaded, setPacketContentLoaded] = useState(!shouldAppendPacket);
  const [packetCoverHtml, setPacketCoverHtml] = useState("");
  const [packetCoverLoaded, setPacketCoverLoaded] = useState(!shouldPrependPacketCover);

  const packetAssetBase = `${process.env.PUBLIC_URL || ""}/packet-html`;

  const normalizePacketHtml = (html, selector = "main.packet-document") => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const main = doc.querySelector(selector) || doc.body;
    let content = main ? main.innerHTML : html;

    return content
      // Packet images now live in the site-root /images/ folder, regardless of whether
      // the app is served from /, /TEST/, /V3.2/, or another PUBLIC_URL path.
      .replace(/src=(["\'])(?:\.\/)?images\//g, `src=$1/images/`)
      .replace(/src=(["\'])[^"']*\/packet-html\/images\//g, `src=$1/images/`)
      .replace(/src=(["\'])[^"']*\/packet_html\/images\//g, `src=$1/images/`)
      .replace(/href=(["\'])css\//g, `href=$1${packetAssetBase}/css/`);
  };

  const renderPacketCoverPage = () => {
    if (!shouldPrependPacketCover || !packetCoverHtml) return null;

    return (
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(packetCoverHtml, {
            ADD_ATTR: ["target", "rel"],
          }),
        }}
      />
    );
  };

  const renderPacketContents = () => {
    if (!shouldAppendPacket || !packetContentHtml) return null;

    return (
      <div
        id="packet-selected-contents"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(packetContentHtml, {
            ADD_ATTR: ["target", "rel"],
          }),
        }}
      />
    );
  };

  useEffect(() => {
    let cancelled = false;

    if (!shouldPrependPacketCover || !selectedPacket?.coverFile) {
      setPacketCoverLoaded(true);
      setPacketCoverHtml("");
      return () => {
        cancelled = true;
      };
    }

    setPacketCoverLoaded(false);

    fetch(`${packetAssetBase}/${selectedPacket.coverFile}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load packet cover: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        if (cancelled) return;
        setPacketCoverHtml(normalizePacketHtml(html));
        setPacketCoverLoaded(true);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setPacketCoverLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shouldPrependPacketCover, selectedPacket, packetAssetBase]);

  useEffect(() => {
    let cancelled = false;

    if (!shouldAppendPacket || !selectedPacket?.contentFile) {
      setPacketContentLoaded(true);
      setPacketContentHtml("");
      return () => {
        cancelled = true;
      };
    }

    setPacketContentLoaded(false);

    fetch(`${packetAssetBase}/${selectedPacket.contentFile}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load packet contents: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        if (cancelled) return;
        setPacketContentHtml(normalizePacketHtml(html));
        setPacketContentLoaded(true);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setPacketContentLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shouldAppendPacket, selectedPacket, packetAssetBase]);

  const waitForPacketImages = (timeoutMs = 25000) => {
    const imageElements = Array.from(document.querySelectorAll("img"));

    const imagePromises = imageElements.map((image) => {
      if (!image.getAttribute("src") && !image.currentSrc) return Promise.resolve();

      try {
        image.loading = "eager";
        image.decoding = "sync";
      } catch (error) {
        // Older browsers may not allow changing these attributes after render.
      }

      if (image.complete && image.naturalWidth > 0) return Promise.resolve();

      if (typeof image.decode === "function") {
        return image.decode().catch(() => undefined);
      }

      return new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    });

    const allImagesReady = Promise.all(imagePromises);
    const timeout = new Promise((resolve) => {
      setTimeout(resolve, timeoutMs);
    });

    return Promise.race([allImagesReady, timeout]);
  };

  const waitForNextPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

  useEffect(() => {
    if (!packetContentLoaded || !packetCoverLoaded) return undefined;

    let cancelled = false;
    let timer;
    const previousTitle = document.title;
    document.title = QuoteFileName();

    const runPrintWhenReady = async () => {
      try {
        await waitForNextPaint();
        await (document.fonts?.ready || Promise.resolve());
        await waitForPacketImages();
        await waitForNextPaint();
      } catch (error) {
        console.warn("Print preload finished with warnings:", error);
      }

      if (!cancelled) {
        window.print();
      }
    };

    timer = setTimeout(runPrintWhenReady, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.title = previousTitle;
    };
  }, [packetContentLoaded, packetCoverLoaded]);

  const QuoteNumber = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear().toString().slice(-2);
    let month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const day = currentDate.getDate().toString().padStart(2, "0");

    let firstThreeLetters;
    if (customerUserData.customerLastName) {
      firstThreeLetters = customerUserData.customerLastName.substring(0, 3);
    }

    const formattedQuoteNumber = `${year}${month}${day}-${
      firstThreeLetters === undefined ? "Name not added" : firstThreeLetters
    }`;
    return formattedQuoteNumber;
  };

  const sanitizeFileNamePart = (value, fallback = "") => {
    const text = String(value || "")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ");

    return text || fallback;
  };

  const getCustomerFileNamePart = () => {
    const businessName = sanitizeFileNamePart(customerUserData.businessName);
    if (businessName) return businessName;

    const lastName = sanitizeFileNamePart(customerUserData.customerLastName, "Customer");
    return `${lastName} Home`;
  };

  const QuoteFileName = () => {
    const quoteNumber = QuoteNumber();
    const optionCount = totalNoOptions || 1;
    const quoteBase = optionCount > 1
      ? `${quoteNumber}-1-${optionCount}`
      : `${quoteNumber}-1`;

    return `${quoteBase} - ${getCustomerFileNamePart()} - Security Camera System Quotes - 👁Platinum CCTV`;
  };

  const getPrintCategoryGroup = (category) => {
    if (
      category === "DVRs & NVRs/NVR Recorders (IP Cameras)" ||
      category === "DVRs & NVRs/NVR Recorders (IP Cameras)@Special/Software and Dongles"
    ) {
      return "NVR Recorder:";
    }

    if (category === "Camera") return "Cameras";
    if (category === "Poe") return "Poe";
    if (["Cabling", "Special", "Hardware"].includes(category)) return "Hardware";
    if (category === "Labor") return "Labor";

    return category;
  };

  const getCategorySortOrder = (category) => {
    const group = getPrintCategoryGroup(category);

    if (group === "NVR Recorder:") return 10;
    if (group === "Cameras") return 20;
    if (group === "Poe") return 30;
    if (group === "Hardware") return 900;
    if (group === "Labor") return 999;

    return 500;
  };

  const getPrintCategories = (cartNumber) => {
    return Array.from(
      new Set(
        combinedData
          .filter((item) => item.option === `${cartNumber}`)
          .map((item) => getPrintCategoryGroup(item.category))
      )
    ).sort((a, b) => getCategorySortOrder(a) - getCategorySortOrder(b));
  };

  const getDisplayCategoryName = (category) => category;

  const getImageSrc = (item) => {
    if (!item.image) {
      return "https://upload.wikimedia.org/wikipedia/commons/d/d1/Image_not_available.png";
    }

    if (String(item.image).startsWith("http")) {
      return item.image;
    }

    return "https://platinumcctv.com/" + item.image;
  };

  const getPrintDisplayName = (item) => getProductDisplayName(item);

  const renderLinkedProductName = (item) => {
    return <strong>{getPrintDisplayName(item)}</strong>;
  };

  const toPrintNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const getPrintQuantity = (item) => {
    return toPrintNumber(
      item.Product_Quantity ||
        item.Camera_Quantity ||
        item.NVR_Quantity ||
        item.Port_Quantity ||
        item.quantity,
      1
    );
  };

  const isCustomPrintItem = (item) => {
    return (
      item.Camera_Category_Name === "Custom" ||
      item.category === "Custom" ||
      item.customItem === true ||
      item.isCustomItem === true
    );
  };

  const getPrintBaseUnitPrice = (item) => {
    if (item.unitprice !== undefined) return toPrintNumber(item.unitprice);
    if (item.pricePerItem !== undefined) return toPrintNumber(item.pricePerItem);
    if (item.Camera_Base_Price !== undefined) return toPrintNumber(item.Camera_Base_Price);
    if (item.basePrice3 !== undefined) return toPrintNumber(item.basePrice3);
    if (item.poeFinalPrice !== undefined) return toPrintNumber(item.poeFinalPrice);
    if (item.Product_Base_Price !== undefined) return toPrintNumber(item.Product_Base_Price);
    return 0;
  };

  const getPrintSavedLineTotal = (item) => {
    const quantity = getPrintQuantity(item);

    if (item.Product_Final_Price !== undefined) {
      const productFinal = toPrintNumber(item.Product_Final_Price);

      // Imported custom items can carry a stale/blank Product_Final_Price of 0
      // even though their recovered unit price is present. Do not let that
      // zero override the recovered custom price only on the print page.
      if (productFinal !== 0 || !isCustomPrintItem(item)) {
        return productFinal;
      }
    }

    if (item.Hardware_New_Final_Price !== undefined) {
      return toPrintNumber(item.Hardware_New_Final_Price);
    }

    if (item.test !== undefined) {
      return toPrintNumber(item.test);
    }

    if (item.Camera_Final_Price !== undefined) {
      const cameraFinal = toPrintNumber(item.Camera_Final_Price);
      if (cameraFinal !== 0 || !isCustomPrintItem(item)) {
        return cameraFinal * quantity;
      }
    }

    if (item.totalPriceForItem !== undefined) {
      const totalPriceForItem = toPrintNumber(item.totalPriceForItem);
      if (totalPriceForItem !== 0 || !isCustomPrintItem(item)) {
        return totalPriceForItem * quantity;
      }
    }

    return null;
  };

  const getPrintUnitPrice = (item) => {
    const quantity = getPrintQuantity(item);
    const savedLineTotal = getPrintSavedLineTotal(item);

    if (savedLineTotal !== null && quantity) {
      return savedLineTotal / quantity;
    }

    return getPrintBaseUnitPrice(item);
  };

  const getPrintLineTotal = (item) => {
    const savedLineTotal = getPrintSavedLineTotal(item);
    if (savedLineTotal !== null) return savedLineTotal;

    return getPrintUnitPrice(item) * getPrintQuantity(item);
  };

  const getOptionItems = (cartNumber) => {
    return combinedData.filter((item) => item.option === `${cartNumber}`);
  };

  const getOptionDiscount = (cartNumber) => {
    const value = Number(optionDiscounts?.[cartNumber] || 0);
    return Number.isFinite(value) ? value : 0;
  };

  const getPartsTotal = (cartNumber) => {
    return getOptionItems(cartNumber)
      .filter((item) => item.category !== "Labor")
      .reduce((total, item) => total + getPrintLineTotal(item), 0);
  };

  const getLaborTotal = (cartNumber) => {
    return getOptionItems(cartNumber)
      .filter((item) => item.category === "Labor")
      .reduce((total, item) => total + getPrintLineTotal(item), 0);
  };

  const PRINT_EXTRA_STANDARD_FIELDS = [
    "page",
    "image",
    "Product_Final_Price",
    "Product_Base_Price",
    "Product_Quantity",
    "cart_bucket",
    "raw_item_json",
    "options_json",
    "globalOption",

    // Cabling internal/raw aliases
    "cableColor",
    "cableColorPrice",
    "cableColour",
    "cableColourPrice",
    "cable_color",
    "cable_color_price",
    "Cable Color_price",

    // PoE internal fields
    "num_poe_ports",
    "num_poe_ports_price",
    "Numb PoE Ports",
    "Numb PoE Ports_price",
    "Number of PoE Ports",
    "Number of PoE Ports_price",
    "Port_Name",
    "Port",
    "Port_Base_Price",
    "Port_Base",
    "Port_Quantity",
    "Port_Quantity_price",
    "poeFinalPrice",
    "poeFinal",
    "Poe Final Price",
    "Poe Final",
    "myUnitPrice",
    "myUnit",
    "My Unit Price",
    "My Unit",

    // Hardware internal fields
    "HardWare_Final_Price",
    "HardWare_Final",
    "Hardware Final Price",
    "Hardware Final",
    "Hardware_New_Final_Price",
    "Hardware_New_Final",
    "Hardware New Final Price",
    "Hardware New Final",
    "test",
    "Test",

    // Special/internal category fields
    "Camera_Category_Name",
    "Camera Category Name",

    // NVR internal/non-display fields
    "option_cameras_selected",
    "option_cameras_selected_price",
    "Option Cameras Selected",
    "Option Cameras Selected_price",
    "licenses",
    "licenses_price",
    "Licenses",
    "Licenses_price",
  ];

  const HIDDEN_PRINT_OPTION_NAMES = new Set([
    "Page",
    "Image",
    "Cable Color Price",
    "No. Of Poe Ports",
    "Number Of Poe Ports",
    "Numb Poe Ports",
    "Port Name",
    "Port",
    "Poe Final Price",
    "Poe Final",
    "My Unit Price",
    "My Unit",
    "Hardware Final Price",
    "Hardware Final",
    "Hardware New Final Price",
    "Hardware New Final",
    "Test",
    "Camera Category Name",
    "Option Cameras Selected",
    "Option Cameras Selected Price",
    "Option Cameras Selected_price",
    "Option Cameras Selected Price",
    "Licenses",
    "Licenses Price",
  ]);

  const getPrintOptionEntries = (item) => {
    return getOptionEntries(item, PRINT_EXTRA_STANDARD_FIELDS).filter(
      (option) =>
        option.name &&
        !HIDDEN_PRINT_OPTION_NAMES.has(option.name) &&
        option.value !== undefined &&
        option.value !== null &&
        option.value !== "" &&
        typeof option.value !== "object"
    );
  };

  const renderPrintDescription = (item) => {
    const options = getPrintOptionEntries(item);

    return (
      <>
        <span className="printProductDescriptionName">
          {renderLinkedProductName(item)}
        </span>
        <br />

        {item.category === "Poe" && item.num_poe_ports ? (
          <>
            <strong>No. of POE Ports - </strong>
            {item.num_poe_ports}
            <br />
          </>
        ) : null}

        {options.map((option, optionIndex) => (
          <React.Fragment key={`${option.name}-${optionIndex}`}>
            <strong>{option.name} - </strong>
            {option.value}{" "}
            <strong>${Number(option.price || 0).toFixed(2)}</strong>
            <br />
          </React.Fragment>
        ))}
      </>
    );
  };

  const renderPacketMaterial = () => {
    if (shouldAppendPacket) return renderPacketContents();
    return null;

    return (
      <div className="printProductClass" style={{ breakBefore: "page", pageBreakBefore: "always" }}>
        <div className="d-flex justify-content-between align-items-start mb-4">
          <img
            src="https://platinumcctv.com/assets/images/CCTV_logo.webp"
            className="img-fluid"
            alt="Logo"
            style={{ minWidth: "150px", maxWidth: "170px" }}
          />
          <div className="text-center flex-grow-1 mx-4">
            <h2 className="fw-bold">{selectedPacket.title}</h2>
            <h4>{selectedPacket.subtitle}</h4>
          </div>
          <div className="printMaxWidth text-end">
            <h6 className="fw-bold mb-1">Quote# {QuoteFileName()}</h6>
            <div className="text-muted small">Quote Version: {quoteVersion}</div>
            <div className="text-muted small">Current Site: {VERSION}</div>
          </div>
        </div>
        <div className="border rounded p-4 mb-4">
          <h4 className="fw-bold mb-3">Included Packet Material</h4>
          <ul className="fs-5">
            {selectedPacket.bullets.map((bullet) => (
              <li key={bullet} className="mb-2">{bullet}</li>
            ))}
          </ul>
        </div>
        <div className="border rounded p-4">
          <h5 className="fw-bold">Brochure</h5>
          <p className="mb-2">The matching brochure is included by reference for PDF packet assembly:</p>
          <a href={selectedPacket.brochureUrl}>{selectedPacket.brochureUrl}</a>
        </div>
      </div>
    );
  };

  return (
    <>
      <div id="packet-print-wrapper">{renderPacketCoverPage()}</div>
      <link
        rel="stylesheet"
        href={`${process.env.PUBLIC_URL || ""}/packet-html/css/packet-fonts.css`}
      />
      <div className="printPageNumber"></div>
    <div className="marginTop printFontSize">
      <div className="container-fluid">
        <div className="row" style={{ fontSize: "17px" }}>
          {/* <div className="col-12 printProductClass">
                        <div className="d-flex justify-content-between align-items-center mb-3 printProductClass">
                            <div className="flex-shrink-0">
                                <img src="https://platinumcctv.com/assets/images/CCTV_logo.webp" className="img-fluid" alt="Platinum CCTV" />
                            </div>
                            <div className="flex-grow-1 text-center mx-5">
                                <div>
                                    <h3 className="text-center printOption fw-bold">{optionDisplayText[1] || "OPTION 1"}</h3>
                                    <p className="text-center printOption">{optionHeading[1] || '\}</p>
                                </div>
                            </div>
                            <div className="flex-shrink-0 printMaxWidth">
                                <h6 className="fw-bold mt-auto printMaxWidth">{`Quote# ${QuoteNumber()}-${whichOption}`}</h6>
                            </div>
                        </div>

                        <div className="d-flex justify-content-between">
                            <div className="d-flex justify-content-between">
                                <div className="me-3">
                                    Platinum CCTV <br />
                                    30W270 Butterfield Rd, #101<br />
                                    Warrenville, IL 60555<br />
                                    630-225-0693
                                </div>

                                <div className="ms-5">
                                    <ul className="list-unstyled">
                                        <li>{customerUserData.businessName || "Business-name"}</li>
                                        <li>
                                            {(customerUserData.customerFirstName || "First-name") + " " + (customerUserData.customerLastName || "Last-name")}
                                        </li>
                                        <li>{customerUserData.phoneNumber || "Phone-number"}</li>
                                        <li>{customerUserData.email || "customerEmail@gmail.com"}</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="d-flex justify-content-between">
                                <div className="me-3">
                                    <h6 className="fw-semibold">Billing Address:</h6>
                                    <ul className="list-unstyled">
                                        <li>{customerUserData.billAddress1 || "Bill-address1"} </li>
                                        <li>{customerUserData.billAddress2 || "Bill-address2"}</li>
                                        <li>{customerUserData.billCity || "Billing City"}</li>
                                        <li>{customerUserData.billState || "Billing State"}</li>
                                        <li>{customerUserData.billZip || "Billing Zip"}</li>

                                    </ul>

                                </div>
                                <div className="ms-3">
                                    <h6 className="fw-semibold">Ship To:</h6>
                                    <ul className="list-unstyled">
                                        <li>{customerUserData.shipAddress1 || ""}</li>
                                        <li>{customerUserData.shipAddress2 || ""}</li>
                                        <li>{customerUserData.shipCity || "Shipping City"}</li>
                                        <li>{customerUserData.shipState || "Shipping State"}</li>
                                        <li>{customerUserData.shipZip || "Shipping Zip"}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div> */}
          <div className="col-12 printProductClass">
            {totalNoOptions &&
              Array.from(
                { length: totalNoOptions },
                (_, cartIndex) => cartIndex + 1
              ).map((cartNumber, index) => {
                // Calculate subtotal for the current option
                const subtotal = combinedData
                  .filter((item) => item.option === `${cartNumber}`)
                  .reduce(
                    (total, item) => total + getPrintLineTotal(item),
                    0
                  ); // Sum Product_Final_Price for each item
                const optionDiscount = getOptionDiscount(cartNumber);
                const optionTotal = Math.max(0, subtotal - optionDiscount);
                return (
                  <div key={cartNumber} className="mt-4 printProductClass">
                    <div className="col-12 printProductClass">
                      <div className="d-flex justify-content-between align-items-center mb-3 printProductClass">
                        <div className="flex-shrink-0">
                          <img
                            src="https://platinumcctv.com/assets/images/CCTV_logo.webp"
                            className="img-fluid"
                            alt="Logo"
                            style={{ minWidth: "150px", maxWidth: "170px" }}
                          />
                        </div>
                        <div className="flex-grow-1 text-center mx-5">
                          <div>
                            <h3 className="text-center printOption fw-bolder">
                              {optionDisplayText[cartNumber] || `OPTION ${cartNumber}`}
                            </h3>
                            <p className="text-center printOption fw-bolder">
                              {optionHeading[cartNumber] || ""}
                            </p>
                          </div>
                        </div>
                        {/* {cartNumber === 1 && */}
                        <div className="flex-shrink-0 printMaxWidth">
                          {/* <h6 className="fw-bold mt-auto printMaxWidth">{`Quote# ${QuoteNumber()}-${whichOption}`}
                                                </h6> */}
                          <h6 className="fw-bold mt-auto mb-1 printMaxWidth">
                            {`Quote# ${QuoteNumber()}-${cartNumber}`}
                          </h6>
                          <div className="text-muted small text-end">Quote Version: {quoteVersion}</div>
                          <div className="text-muted small text-end">Current Site: {VERSION}</div>
                        </div>
                        {/* } */}
                      </div>

                      <div className="d-flex justify-content-between">
                        <div className="d-flex justify-content-between w-100 gap-3">
                          <div className="me-3 printContactInfo">
                            Platinum CCTV <br />
                            30W270 Butterfield Rd, #101
                            <br />
                            Warrenville, IL 60555
                            <br />
                            630-225-0693
                          </div>

                          {/* </div>
                        <div className="d-flex justify-content-between"> */}
                          <div className="me-3 printClientInfo">
                            <h6 className="customFS-12px fw-semibold printClientHeadingHidden">
                              Client Info:
                            </h6>
                            <ul className="list-unstyled">
                              <li>{customerUserData.businessName || ""}</li>
                              <li>
                                {`${customerUserData.customerFirstName || ""} ${
                                  customerUserData.customerLastName || ""
                                }`.trim()}
                              </li>
                              <li>{customerUserData.phoneNumber || ""}</li>
                              <li>{customerUserData.email || ""}</li>
                            </ul>
                          </div>
                          <div className="me-3 printClientInfo">
                            <h6 className="customFS-12px fw-semibold printAddressHeading">
                              Billing Address:
                            </h6>
                            <ul className="list-unstyled">
                              <li>{customerUserData.billAddress1 || ""}</li>
                              <li>{customerUserData.billAddress2 || ""}</li>
                              <li>
                                {[
                                  customerUserData.billCity,
                                  customerUserData.billState,
                                  customerUserData.billZip,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </li>
                            </ul>
                          </div>
                          <div className="ms-3 printClientInfo">
                            <h6 className="customFS-12px fw-semibold printAddressHeading">
                              Ship To:
                            </h6>
                            <ul className="list-unstyled">
                              <li>{customerUserData.shipAddress1 || ""}</li>
                              <li>{customerUserData.shipAddress2 || ""}</li>
                              <li>
                                {[
                                  customerUserData.shipCity,
                                  customerUserData.shipState,
                                  customerUserData.shipZip,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                    <br />
                    {/* {cartNumber !== 1 &&
                                        <div className="">
                                            <h3 className="text-center printOption">Option {cartNumber}</h3>
                                            <p className="text-center">{optionHeading[cartNumber] || 'Heading'}</p>
                                        </div>
                                    } */}

                    {getPrintCategories(cartNumber).map((category, categoryIndex) => {
                      const categoryItems = combinedData
                        .filter((item) => item.option === `${cartNumber}`)
                        .filter(
                          (item) => getPrintCategoryGroup(item.category) === category
                        );

                      return categoryItems.length > 0 ? (
                        <div
                          key={categoryIndex}
                          className="col-12 printProductClass"
                        >
                          {/* Display the heading only if items exist */}
                          <div className="heading-margin printProductClass printBackground">
                            <div className="d-flex justify-content-between">
                              <h4 className="my-1 fs-6 printProductClass">
                                {/* {category ===
                                  "DVRs & NVRs/NVR Recorders (IP Cameras)"
                                  ? "NVR"
                                  : category} */}
                                {getDisplayCategoryName(category)}
                              </h4>
                            </div>
                          </div>
                          <div className="printProductClass">
                            {/* Table Header - Rendered Once */}
                            <Table id="customers">
                              <tbody>
                                {category === "NVR Recorder:" ? (
                                  <tr className="printColumnLabelRow">
                                    <td className="th-options"></td>
                                    <td className="th-sku printSkuCell">SKU</td>
                                    <td className="th-description printDescriptionCell">
                                      Description & Options
                                    </td>
                                    <td className="th-price printPriceCell">
                                      Price (with options)
                                    </td>
                                    <td className="th-qty printQtyCell">Qty</td>
                                    <td className="th-total printTotalCell">
                                      Item Total
                                    </td>
                                  </tr>
                                ) : null}
                                {/* {console.log("categoryItems", categoryItems)} */}
                                {/* Map through categoryItems to render each product row */}
                                {categoryItems.map((item, idx) => {
                                  return (
                                    <tr key={idx} className="paddingTest">
                                      <td className="th-options">
                                        <img
                                          className="printImg itemImage"
                                          style={{
                                            height: "auto",
                                          }}
                                          src={getImageSrc(item)}
                                          alt="Item"
                                        />
                                      </td>
                                      <td className="th-sku printSkuCell">
                                        {getProductSku(item)}
                                      </td>
                                      <td className="th-description printDescriptionCell">
                                        {renderPrintDescription(item)}
                                      </td>
                                      <td className="th-price printPriceCell">
                                        $
                                        {getPrintUnitPrice(item).toFixed(2)}
                                      </td>
                                      <td className="th-qty printQtyCell">
                                        {getPrintQuantity(item)}
                                      </td>
                                      <td className="th-total printTotalCell">
                                        $
                                        {getPrintLineTotal(item).toFixed(2)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      ) : null; // Don't render if no items exist
                    })}
                    <div className="mt-4 printProductClass">
                      <div className="printTotalsDisclaimerRow mb-3">
                        <div className="grey-text printDisclaimer">
                          The information provided in this quotation is based on
                          current supply chain costs and may vary at any time until
                          full deposit is received (cost of equipment). At that time,
                          the cost of the product will be locked in. Supply chain
                          cost increases may include tariffs or price increases
                          imposed by global trade changes or shipping costs. While
                          we try to honor quotations, these factors are outside of
                          our control. This is why we charge a deposit equal to the
                          cost of the equipment. At that time, we reserve all your
                          product and prepare it for installation or shipment. We can
                          refresh your quote at any time. Labor rates are based on
                          the visible condition of the facility at time of walkthrough
                          (or description of facility for remote quote). If additional
                          obstacles are encountered, we will discuss options at the
                          time they are encountered and provide additional quote if
                          any additional costs would be incurred. Labor rates are
                          based on normal business hours except where noted. Lift
                          rental is not included except where noted, customer may
                          provide lift or lift can be provided at additional cost if
                          necessary for installation.
                        </div>
                        <div className="text-end fw-bold printTotalsBlock">
                          <div>Parts Total: $ {getPartsTotal(cartNumber).toFixed(2)}</div>
                          <div>Labor Total: $ {getLaborTotal(cartNumber).toFixed(2)}</div>
                          {optionDiscount > 0 ? (
                            <div>Discount: -$ {optionDiscount.toFixed(2)}</div>
                          ) : null}
                          <div className="printGrandTotal">
                            Total: $ {optionTotal.toFixed(2)}
                          </div>
                          <div style={{ fontSize: "10px", fontWeight: "normal" }}>
                            *Not including sales tax (where applicable)
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1 me-3 printDescription printProductClass">
                        <h6 className="fw-semibold">
                          Details about this Option:
                        </h6>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              optionDescription[cartNumber] || ""
                            ),
                          }}
                        ></div>
                        {/* <p>{optionDescription[cartNumber]?.replace(/<[^>]+>/g, '') || ''}</p> */}
                      </div>
                    </div>
                    {/* Add the page break after each cart if it's not the last one */}
                    {index < totalNoOptions - 1 && (
                      <div className="cart-break"></div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
      {shouldAppendPacket ? <div className="cart-break"></div> : null}
      <div id="packet-print-wrapper">{renderPacketMaterial()}</div>
    </>
  );
};

export default HigherOrderComponent(Print);